import { Schema, Predicate } from 'effect';
import { HttpClientError } from 'effect/http';
import { DocumentInsightsFailureReason } from '@listening/api-contract/document-insights';
import { PlanChangePreview } from '@listening/api-contract/schemas/billing-manage';
import { CloudStorageProviderSchema } from '@listening/api-contract/integration-providers';

export class InternalServerError extends Schema.TaggedError<InternalServerError>()(
  'InternalServerError',
  {
    message: Schema.String,
  },
  { httpApiStatus: 500 },
) {}

export class DocumentInsightsFailedError extends Schema.TaggedError<DocumentInsightsFailedError>()(
  'DocumentInsightsFailedError',
  {
    message: Schema.String,
    reason: Schema.optional(DocumentInsightsFailureReason),
  },
  { httpApiStatus: 422 },
) {}

export class BadRequestError extends Schema.TaggedError<BadRequestError>()(
  'BadRequestError',
  {
    message: Schema.String,
  },
  { httpApiStatus: 400 },
) {}

export class UnauthorizedError extends Schema.TaggedError<UnauthorizedError>()(
  'UnauthorizedError',
  {
    message: Schema.String,
  },
  { httpApiStatus: 401 },
) {}

export class NotFoundError extends Schema.TaggedError<NotFoundError>()(
  'NotFoundError',
  {
    message: Schema.String,
  },
  { httpApiStatus: 404 },
) {}

export class ConflictError extends Schema.TaggedError<ConflictError>()(
  'ConflictError',
  {
    message: Schema.String,
  },
  { httpApiStatus: 409 },
) {}

export class IntegrationReconnectRequiredError extends Schema.TaggedError<IntegrationReconnectRequiredError>()(
  'IntegrationReconnectRequiredError',
  {
    message: Schema.String,
    provider: CloudStorageProviderSchema,
  },
  { httpApiStatus: 409 },
) {}

/**
 * A plan change's cost moved between preview and confirm. Carries the fresh
 * preview so the client re-renders the confirmation without a second request.
 */
export class PlanChangePreviewStaleError extends Schema.TaggedError<PlanChangePreviewStaleError>()(
  'PlanChangePreviewStaleError',
  {
    message: Schema.String,
    preview: PlanChangePreview,
  },
  { httpApiStatus: 409 },
) {}

export class FileTooLargeError extends Schema.TaggedError<FileTooLargeError>()(
  'FileTooLargeError',
  {
    message: Schema.String,
    max_file_size_bytes: Schema.Int,
    received_file_size_bytes: Schema.Int,
  },
  { httpApiStatus: 413 },
) {}

export class AudioRechunkingError extends Schema.TaggedError<AudioRechunkingError>()(
  'AudioRechunkingError',
  {
    message: Schema.String,
    audioId: Schema.String,
  },
  { httpApiStatus: 503 },
) {}

export class DocumentInaccessibleError extends Schema.TaggedError<DocumentInaccessibleError>()(
  'DocumentInaccessibleError',
  {
    message: Schema.String,
    url: Schema.String,
    status_code: Schema.NullOr(Schema.Int),
  },
  { httpApiStatus: 422 },
) {}

export class UploadQuotaExceededError extends Schema.TaggedError<UploadQuotaExceededError>()(
  'UploadQuotaExceededError',
  {
    message: Schema.String,
    max_uploads: Schema.Int,
    reset_at_ms: Schema.Int,
  },
  { httpApiStatus: 429 },
) {}

export class UploadDocumentTypeNotAllowedError extends Schema.TaggedError<UploadDocumentTypeNotAllowedError>()(
  'UploadDocumentTypeNotAllowedError',
  {
    message: Schema.String,
    document_type: Schema.String,
  },
  { httpApiStatus: 403 },
) {}

/**
 * Generic 429 used when an UPSTREAM dependency tells us the caller (or
 * we, as a proxy) have exceeded their limit — e.g. Zotero, Stripe,
 * OpenAI. We don't know the upstream's retry policy, so callers should
 * back off with their own default cadence.
 *
 * For our own API rate-limiter (the one in `apps/api/src/services/
 * rate-limit.ts`), use `RateLimitedError` instead — it carries the
 * `retry_after_seconds` we computed and is a distinct tag so clients can
 * tell "you hit our limit" from "an upstream hit its limit".
 */
export class TooManyRequestsError extends Schema.TaggedError<TooManyRequestsError>()(
  'TooManyRequestsError',
  {
    message: Schema.String,
  },
  { httpApiStatus: 429 },
) {}

/**
 * Emitted by our own rate limiter when the caller exceeds a configured
 * window. Distinct from `TooManyRequestsError` (which is upstream-driven)
 * so clients can attribute the throttle correctly.
 */
export class RateLimitedError extends Schema.TaggedError<RateLimitedError>()(
  'RateLimitedError',
  {
    message: Schema.String,
    scope: Schema.optional(Schema.Literals(['identity', 'global'])),
    reason: Schema.optional(Schema.Literals(['exceeded', 'store-error'])),
    /**
     * Seconds the caller should wait before retrying — mirrors the standard
     * `Retry-After` HTTP header semantics. Zero when we can't compute a
     * wait (e.g. Valkey store error).
     */
    retry_after_seconds: Schema.Finite,
  },
  { httpApiStatus: 429 },
) {}

/**
 * The caller's CAPTCHA token failed verification (missing, expired, or
 * rejected by Cloudflare Turnstile siteverify). Distinct from
 * `UnauthorizedError` (auth credential problem) and `BadRequestError`
 * (malformed payload) so clients can prompt the user to retry the widget
 * without surfacing it as a generic 400.
 *
 * `reason` carries the Turnstile error code (e.g. `invalid-input-response`,
 * `timeout-or-duplicate`) when available — useful for client telemetry,
 * not for end-user display.
 *
 * @see apps/api/src/services/turnstile.ts
 */
export class CaptchaError extends Schema.TaggedError<CaptchaError>()(
  'CaptchaError',
  {
    message: Schema.String,
    reason: Schema.optional(Schema.String),
  },
  { httpApiStatus: 403 },
) {}

export class BadGatewayError extends Schema.TaggedError<BadGatewayError>()(
  'BadGatewayError',
  {
    message: Schema.String,
  },
  { httpApiStatus: 502 },
) {}

/**
 * A retryable "the upstream is temporarily unavailable" failure — e.g. an
 * upstream dependency rate-limited us (429) or 5xx'd after we exhausted our
 * own bounded retries. Distinct from `InternalServerError` (500, a genuine
 * bug/hard failure) so a transient upstream blip does NOT page as a 500 and
 * so a CDN can serve stale content (`stale-if-error`) instead. `503` is the
 * correct retryable status; clients should back off and retry.
 */
export class ServiceUnavailableError extends Schema.TaggedError<ServiceUnavailableError>()(
  'ServiceUnavailableError',
  {
    message: Schema.String,
  },
  { httpApiStatus: 503 },
) {}

export const integrationProviderErrors = [
  BadRequestError,
  NotFoundError,
  IntegrationReconnectRequiredError,
  BadGatewayError,
  ServiceUnavailableError,
  InternalServerError,
] as const;

/**
 * Wire-compatible replica of Effect v3's `HttpApiDecodeError` — the 400 body
 * every endpoint automatically declared (and emitted on request-decode
 * failure) under `@effect/platform` v3. v4 replaced it with the non-schema
 * `HttpApiSchemaError`, which renders an EMPTY 400 — a silent wire change to
 * the frozen v2 contract. This class restores the declared surface; it is
 * attached to every endpoint via `RequestDecodeErrorMiddleware` (middleware.ts),
 * and apps/api must emit it via `HttpApiMiddleware.layerSchemaErrorTransform`.
 * Field shapes/order mirror the v3 OpenAPI document's HttpApiDecodeError/
 * Issue/PropertyKey definitions, captured off v3 before the migration.
 * v3 decoded the symbol path branch to a real `Symbol.for(key)`; this replica
 * keeps the wire struct form instead, which round-trips identically.
 */
export const DecodeIssueSymbolKey = Schema.TaggedStruct('symbol', {
  key: Schema.String,
});
const DecodeIssuePropertyKey = Schema.Union([
  Schema.String,
  Schema.Int,
  DecodeIssueSymbolKey,
]);

const DecodeIssue = Schema.Struct({
  _tag: Schema.Literals([
    'Pointer',
    'Unexpected',
    'Missing',
    'Composite',
    'Refinement',
    'Transformation',
    'Type',
    'Forbidden',
  ]),
  path: Schema.Array(DecodeIssuePropertyKey),
  message: Schema.String,
});

export class HttpApiDecodeError extends Schema.TaggedError<HttpApiDecodeError>()(
  'HttpApiDecodeError',
  {
    issues: Schema.Array(DecodeIssue),
    message: Schema.String,
  },
  {
    httpApiStatus: 400,
    description: 'The request did not match the expected schema',
  },
) {}

/**
 * Tags of every domain error this contract puts on an endpoint's error channel.
 * Each such error carries a backend-authored `message`. Clients use this to
 * tell a known, user-displayable failure apart from a transport/decode error
 * (`@effect/platform` `RequestError`/`ResponseError`, schema `ParseError`)
 * whose `message` is technical and should fall back to a generic localized
 * string. Keep in sync with the classes above — a missing tag just means that
 * error degrades to the generic fallback client-side, never a crash.
 */
const apiDomainErrorTags = [
  'InternalServerError',
  'DocumentInsightsFailedError',
  'BadRequestError',
  'UnauthorizedError',
  'NotFoundError',
  'ConflictError',
  'IntegrationReconnectRequiredError',
  'PlanChangePreviewStaleError',
  'FileTooLargeError',
  'AudioRechunkingError',
  'DocumentInaccessibleError',
  'UploadQuotaExceededError',
  'UploadDocumentTypeNotAllowedError',
  'TooManyRequestsError',
  'RateLimitedError',
  'CaptchaError',
  'BadGatewayError',
  'ServiceUnavailableError',
] as const;

/**
 * Type guard: is `u` one of this contract's domain tagged errors? When true,
 * `u.message` is the backend-authored, user-displayable copy for surfacing API
 * failure copy in the UI.
 */
export const isApiDomainError = Schema.is(
  Schema.Struct({
    _tag: Schema.Literals(apiDomainErrorTags),
    message: Schema.String,
  }),
);

/**
 * Type guard: does `u` represent a 401 / unauthenticated failure?
 *
 * IMPORTANT: the Effect HttpApi client decodes a 401 into a *tagged* error, NOT
 * a transport `ResponseError`. String-matching `ResponseError` (the pre-Effect
 * fetch-client pattern) silently never fires. The two tagged 401 shapes are:
 *   - `@effect/platform` `HttpApiError.Unauthorized` (`_tag: "Unauthorized"`),
 *     emitted by `UserAuthMiddleware` for an expired/invalid/missing token
 *     (the common "re-auth needed" case — see middleware.ts `AuthMiddlewareFailure`).
 *   - our `UnauthorizedError` (`_tag: "UnauthorizedError"`), emitted by handlers
 *     that `.addError(UnauthorizedError)` for a handler-level permission denial.
 * A raw `ResponseError` with status 401 is only reachable for an endpoint that
 * doesn't map 401 (none of our authed endpoints do) — handled defensively for
 * any non-`eq` transport call.
 */
export const isUnauthorizedError = (u: unknown): boolean => {
  if (
    Predicate.isTagged(u, 'Unauthorized') ||
    Predicate.isTagged(u, 'UnauthorizedError')
  ) {
    return true;
  }
  if (HttpClientError.isHttpClientError(u)) {
    // v4's `HttpClientError` is one wrapper class (`_tag` is always
    // "HttpClientError"); the request/response-side variant now lives in
    // `.reason`, and `.response` is a getter that is `undefined` unless a
    // response was actually received (StatusCodeError/DecodeError/EmptyBodyError).
    return u.response?.status === 401;
  }
  return false;
};

/** True only for auth middleware failure, not handler-level permission denial. */
export const isSessionAuthenticationError = (u: unknown): boolean =>
  Predicate.isTagged(u, 'Unauthorized');

/**
 * Schema union of every domain tagged error this contract can put on an
 * endpoint's error channel. Because each member is a `Schema.TaggedError`, this
 * union is itself a `Schema` — so a caught error can be `Schema.encode`d to
 * plain JSON for transport and `Schema.decode`d back into the *real* error
 * instance on the other side (with `instanceof` + field validation). Used to
 * cross the browser-extension messaging boundary, whose serialize-error layer
 * can copy `_tag` but cannot reconstruct class instances. Keep in sync with
 * `apiDomainErrorTags`.
 */
export const ApiDomainError = Schema.Union([
  InternalServerError,
  DocumentInsightsFailedError,
  BadRequestError,
  UnauthorizedError,
  NotFoundError,
  ConflictError,
  IntegrationReconnectRequiredError,
  PlanChangePreviewStaleError,
  FileTooLargeError,
  AudioRechunkingError,
  DocumentInaccessibleError,
  UploadQuotaExceededError,
  UploadDocumentTypeNotAllowedError,
  TooManyRequestsError,
  RateLimitedError,
  CaptchaError,
  BadGatewayError,
  ServiceUnavailableError,
]);
export type ApiDomainError = typeof ApiDomainError.Type;
export type EncodedApiDomainError = Schema.Codec.Encoded<typeof ApiDomainError>;

/**
 * Guard for domain errors the typed client actually constructed. `Schema.is`
 * over a union of `Schema.TaggedError` classes tests identity, not shape: a
 * plain `{ _tag: 'NotFoundError', message }` fails it and is classified as if
 * it were a transport failure. That is deliberate — every caller reads a typed
 * error channel fed by `HttpApiClient`, so a structural-only domain error means
 * a value crossed a boundary without being `Schema.decode`d, and the fix
 * belongs at that boundary. `isApiDomainError` above is the structural guard,
 * for values that legitimately arrive as plain JSON.
 */
const isApiDomainErrorInstance = Schema.is(ApiDomainError);

/**
 * Coerce any caught value into a member of `ApiDomainError` so it can cross a
 * Schema-encoded transport boundary (encode never fails). A 401 (the tagged
 * `Unauthorized` from UserAuthMiddleware, our `UnauthorizedError`, or a raw
 * transport 401) collapses to `UnauthorizedError`; any other recognized domain
 * error passes through; everything else (transport / parse / unexpected)
 * collapses to `InternalServerError` carrying the original message.
 */
export const toApiDomainError = (u: unknown): ApiDomainError => {
  if (isUnauthorizedError(u)) {
    return UnauthorizedError.make({ message: 'Unauthorized' });
  }
  if (isApiDomainErrorInstance(u)) {
    return u;
  }
  return InternalServerError.make({
    message: u instanceof Error ? u.message : String(u),
  });
};

/**
 * How a client should treat a failed call.
 *
 * - `permanent` — the request can never succeed as written (a contract error
 *   not listed as transient, the server's `HttpApiDecodeError` request-schema
 *   400, an integration that must be reconnected, or a raw status outside
 *   `HttpClient.retryTransient`'s transient set). Retrying only burns quota;
 *   the caller should surface or skip.
 * - `session` — the credential is the problem (401, from either the auth
 *   middleware's `Unauthorized` or a handler's `UnauthorizedError`). Retrying
 *   cannot help until the session is repaired.
 * - `transient` — a raw 408, 429, 500, 502, 503 or 504 (the same set as
 *   `HttpClient.retryTransient`), and failures with no response (transport,
 *   decode, defects): retrying may help.
 */
export const ApiRetryClass = Schema.Literals([
  'transient',
  'permanent',
  'session',
]);
export type ApiRetryClass = typeof ApiRetryClass.Type;

const transientDomainErrorTags: ReadonlySet<ApiDomainError['_tag']> = new Set([
  'InternalServerError',
  'AudioRechunkingError',
  'TooManyRequestsError',
  'RateLimitedError',
  'BadGatewayError',
  'ServiceUnavailableError',
]);

const isHttpApiDecodeError = Schema.is(HttpApiDecodeError);

const transientStatuses = new Set([408, 429, 500, 502, 503, 504]);

const statusRetryClass = (status: number): ApiRetryClass =>
  transientStatuses.has(status) ? 'transient' : 'permanent';

/**
 * Classifies a caught failure for retry policy. A contract error is transient
 * only when it means "try later"; a new contract error is permanent until
 * listed above. A raw transport error is classified by its response status.
 */
export const apiRetryClass = (error: unknown): ApiRetryClass => {
  if (isUnauthorizedError(error)) return 'session';
  if (isApiDomainErrorInstance(error))
    return transientDomainErrorTags.has(error._tag) ? 'transient' : 'permanent';
  if (isHttpApiDecodeError(error)) return 'permanent';
  if (HttpClientError.isHttpClientError(error) && error.response)
    return statusRetryClass(error.response.status);
  return 'transient';
};

/**
 * Server-directed backoff, in milliseconds, when the failure carries one.
 * Only our own rate limiter reports a wait — `TooManyRequestsError` is
 * upstream-driven and carries none, so the caller uses its own cadence.
 */
export const apiRetryAfterMs = (error: unknown): number | undefined => {
  if (!isApiDomainErrorInstance(error)) return undefined;
  if (!Predicate.isTagged(error, 'RateLimitedError')) return undefined;
  return error.retry_after_seconds > 0
    ? error.retry_after_seconds * 1000
    : undefined;
};
